import { AfterViewInit, Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ChannelCategoryService } from '@service/channel/channel-category.service';
import { ChannelGroupService } from '@service/channel/channel-group.service';
import { ChannelSheetService } from '@service/channel/channel-sheet.service';
import {
    DialogChannelCategoryComponent,
    DialogType,
} from '../dialog-channel-category/dialog-channel-category.component';
import { DialogChannelGroupComponent } from '../dialog-channel-group/dialog-channel-group.component';
import { MatDialog } from '@angular/material/dialog';
import { ChannelGroup } from 'icreate/common/models/channel-group';
import { ChannelCategory } from 'icreate/common/models/channel-category';
import { lastValueFrom, Subject, takeUntil } from 'rxjs';
import { VesselService } from '@service/vessel.service';
import { parseExcelRow } from '@service/channel/channel-utils';
import { Channel } from 'icreate/common/models/channel';
import { ChannelService } from '@service/channel/channel.service';
import { MAX_GROUPS_COUNT } from 'icreate/common/models/channel-config';

@Component({
    selector: 'app-side-menu',
    templateUrl: './side-menu.component.html',
    styleUrls: ['./side-menu.component.scss'],
    standalone: false,
})
export class ChannelSideMenuComponent implements OnInit, AfterViewInit, OnDestroy {
    @ViewChild('SideMenuContainer') SideMenuContainer: ElementRef;

    readonly GROUP_MODE = ChannelPanel.GROUP;
    readonly CATEGORY_MODE = ChannelPanel.CATEGORY;
    readonly ALARM_MODE = ChannelPanel.ALARM;

    readonly ADD = DialogType.add;

    isShowAddOption = false;
    showSideMenu = true;
    showAddMenu = false;
    tabState: ChannelPanel;

    channelGroupList: ChannelGroup[] = [];
    selectedChannelId: string;
    selectedChannel: Channel;
    activeGroup: ChannelGroup;
    canEditSheet: boolean = true;

    private destroySubscriptionSubject$ = new Subject<void>();

    constructor(
        private channelGroupService: ChannelGroupService,
        private channelCategoryService: ChannelCategoryService,
        private channelSheetService: ChannelSheetService,
        private channelService: ChannelService,
        private dialog: MatDialog,
        private vesselService: VesselService
    ) {}

    @HostListener('document:click', ['$event'])
    clickOut(event: MouseEvent) {
        if (this.showAddMenu) {
            this.showAddMenu = false;
        }
    }

    async ngOnInit() {
        this.channelSheetService.setTabState(this.GROUP_MODE);

        this.vesselService.accessUpdated$.pipe(takeUntil(this.destroySubscriptionSubject$)).subscribe(canEdit => {
            this.canEditSheet = canEdit;
        });

        this.channelSheetService.tabState$.pipe(takeUntil(this.destroySubscriptionSubject$)).subscribe(tabState => {
            this.tabState = tabState;
        });

        this.channelSheetService.selectedChannel$
            .pipe(takeUntil(this.destroySubscriptionSubject$))
            .subscribe(async selectedChannel => {
                if (selectedChannel) {
                    const channel = Array.isArray(selectedChannel) ? parseExcelRow(selectedChannel) : selectedChannel;

                    if (this.selectedChannelId !== channel.rowId) {
                        this.selectedChannelId = channel.rowId;
                        this.selectedChannel = channel;
                    }
                } else {
                    this.selectedChannelId = undefined;
                    this.selectedChannel = undefined;
                }
            });

        this.channelSheetService.activeGroup$
            .pipe(takeUntil(this.destroySubscriptionSubject$))
            .subscribe(async activeGroup => {
                this.activeGroup = activeGroup;
            });

        this.channelGroupService.list$.pipe(takeUntil(this.destroySubscriptionSubject$)).subscribe(channelGroupList => {
            this.channelGroupList = channelGroupList;
        });
    }

    ngAfterViewInit(): void {
        this.channelSheetService.setSideBarElement(this.SideMenuContainer);
    }

    ngOnDestroy() {
        this.destroySubscriptionSubject$.next();
        this.destroySubscriptionSubject$.complete();
    }

    expansionButtonClick() {
        this.showSideMenu = !this.showSideMenu;
    }

    tabClick(tabIndex: ChannelPanel) {
        this.channelSheetService.setTabState(tabIndex);
    }

    async addMenuGroupClick() {
        if (!this.canEditSheet || !this.canAddNewGroup()) return;

        const dialogRef = this.dialog.open(DialogChannelGroupComponent, {
            width: '474px',
            data: {
                title: 'channel.add-group',
                type: DialogType.add,
            },
        });

        const groupToAdd: ChannelGroup = await lastValueFrom(dialogRef.afterClosed());
        if (groupToAdd) {
            try {
                const vesselID = this.vesselService.getActiveVesselId();
                const newGroup = await this.channelGroupService.add(groupToAdd, vesselID);
                const channelList = this.channelSheetService.initChannelRows(newGroup);
                await this.channelService.create(channelList);
                this.channelSheetService.setActiveGroup(newGroup);
            } catch (err) {
                console.error(err);
            } finally {
                this.channelSheetService.hideLoading();
            }
        }
    }

    addMenuCategoryClick() {
        if (!this.canEditSheet) return;

        const dialogRef = this.dialog.open(DialogChannelCategoryComponent, {
            width: '474px',
            data: {
                title: 'channel.add-category',
                type: DialogType.add,
            },
        });
        const subscribeDialog = dialogRef.componentInstance.onAddCategory.subscribe(
            async (channelCategory: ChannelCategory) => {
                this.channelSheetService.showLoading();
                try {
                    const vesselID = this.vesselService.getActiveVesselId();
                    await this.channelCategoryService.add(channelCategory, vesselID);
                } catch (err) {
                    console.error(err);
                } finally {
                    this.channelSheetService.hideLoading();
                }
            }
        );
        dialogRef.afterClosed().subscribe(() => {
            subscribeDialog.unsubscribe();
        });
    }

    addIconClick() {
        if (!this.canEditSheet) return;
        this.showAddMenu = !this.showAddMenu;
    }

    toggleAddOption() {
        if (!this.canEditSheet) return;
        this.isShowAddOption = this.isShowAddOption ? false : true;
    }

    canAddNewGroup() {
        return this.channelGroupList.length < MAX_GROUPS_COUNT || !this.canEditSheet;
    }
}

export enum ChannelPanel {
    GROUP = 'groups',
    CATEGORY = 'categories',
    ALARM = 'alarms',
}
